import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { ref, get, set } from 'firebase/database';
import SEO from '../components/SEO';
import TemplateSelector from '../components/TemplateSelector';
import SaveAsTemplate from '../components/SaveAsTemplate';
import MediaPicker from '../components/MediaPicker';

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */
interface Button { id: string; title: string; payload: string; }

interface ResponseItem {
  id: string;
  type: 'text' | 'buttons' | 'image' | 'video' | 'link' | 'ask' | 'handoff' | 'payment';
  content: string;
  caption?: string;
  url?: string;
  linkLabel?: string;
  buttons?: Button[];
  askVariable?: string;
  askResponses?: ResponseItem[];
  handoffMessage?: string;
  handoffEmail?: string;
  paymentMethod?: 'paynow' | 'stripe' | 'cash' | 'bank';
  amount?: string;
  currency?: string;
  itemName?: string;
  description?: string;
}

interface Trigger {
  trigger: string;
  keywords: string[];
  responses: ResponseItem[];
}

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const S = {
  page: { background: 'var(--bg-page)', minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" } as React.CSSProperties,
  input: { width: '100%', padding: '9px 12px', background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const },
  textarea: { width: '100%', padding: '9px 12px', background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', resize: 'vertical' as const, boxSizing: 'border-box' as const },
  card: { background: 'var(--bg-card)', borderRadius: '14px', border: '1px solid var(--bg-element)', padding: '20px', marginBottom: '16px' } as React.CSSProperties,
  respCard: { background: 'var(--bg-sidebar)', borderRadius: '10px', border: '1px solid var(--bg-element)', padding: '16px', marginBottom: '12px' } as React.CSSProperties,
  label: { display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase' as const, marginBottom: '6px' },
  btnPrimary: { padding: '8px 18px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' } as React.CSSProperties,
  btnDanger: { padding: '6px 12px', background: '#ef444420', color: '#ef4444', border: '1px solid #ef444440', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' } as React.CSSProperties,
  btnGhost: { padding: '6px 14px', background: 'var(--bg-element)', color: 'var(--text-secondary)', border: '1px solid var(--bg-element)', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' } as React.CSSProperties,
  btnGreen: { padding: '6px 14px', background: '#10b98120', color: '#10b981', border: '1px solid #10b98140', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' } as React.CSSProperties,
};

const TYPE_COLORS: Record<string, string> = {
  text: '#10b981', buttons: '#f59e0b', image: '#3b82f6',
  video: '#8b5cf6', link: '#06b6d4', ask: '#ec4899', handoff: '#ef4444', payment: '#a855f7',
};

/* ─────────────────────────────────────────────
   RESPONSE EDITOR
───────────────────────────────────────────── */
function ResponseEditor({ resp, rIdx, allVariables, onUpdate, onDelete, onMoveUp, onAddButton, onUpdateButton, onDeleteButton, onAddAskResponse, onUpdateAskResponse, onDeleteAskResponse }: {
  resp: ResponseItem; rIdx: number; allVariables: string[];
  onUpdate: (f: string, v: any) => void; onDelete: () => void; onMoveUp: () => void;
  onAddButton: () => void; onUpdateButton: (bIdx: number, f: string, v: string) => void; onDeleteButton: (bIdx: number) => void;
  onAddAskResponse: () => void; onUpdateAskResponse: (fIdx: number, f: string, v: any) => void; onDeleteAskResponse: (fIdx: number) => void;
}) {
  const color = TYPE_COLORS[resp.type] || '#64748b';

  return (
    <div style={{ ...S.respCard, borderLeft: `3px solid ${color}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ background: color + '20', color, fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '10px' }}>#{rIdx + 1}</span>
          <select value={resp.type} onChange={e => onUpdate('type', e.target.value)}
            style={{ ...S.input, width: 'auto', padding: '5px 10px', fontSize: '12px', color, appearance: 'none' as const }}>
            <option value="text">💬 Text message</option>
            <option value="buttons">🔘 Buttons (choice)</option>
            <option value="image">🖼️ Image</option>
            <option value="video">🎬 Video</option>
            <option value="link">🔗 Link / CTA button</option>
            <option value="ask">❓ Ask visitor a question</option>
            <option value="handoff">🙋 Hand off to human</option>
            <option value="payment">💳 Payment request</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {rIdx > 0 && <button onClick={onMoveUp} style={S.btnGhost}>↑</button>}
          <button onClick={onDelete} style={S.btnDanger}>Delete</button>
        </div>
      </div>

      {resp.type === 'text' && (
        <div>
          <textarea value={resp.content} onChange={e => onUpdate('content', e.target.value)} rows={3}
            placeholder="What should the bot say? Use {{variableName}} to insert collected answers." style={S.textarea} />
          {allVariables.length > 0 && (
            <div style={{ marginTop: '8px', padding: '10px', background: '#10b98110', borderRadius: '8px', border: '1px solid #10b98130' }}>
              <div style={{ fontSize: '11px', color: '#10b981', marginBottom: '6px', fontWeight: '600' }}>Insert collected variable</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {allVariables.map(v => (
                  <button key={v} onClick={() => onUpdate('content', resp.content + `{{${v}}}`)}
                    style={{ padding: '3px 8px', background: '#10b981', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '11px' }}>
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {resp.type === 'buttons' && (
        <div>
          <textarea value={resp.content} onChange={e => onUpdate('content', e.target.value)} rows={2}
            placeholder="Message shown above the buttons" style={{ ...S.textarea, marginBottom: '12px' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={S.label}>Buttons (max 3)</label>
            <button onClick={onAddButton} disabled={!!(resp.buttons && resp.buttons.length >= 3)}
              style={{ ...S.btnGhost, opacity: resp.buttons && resp.buttons.length >= 3 ? 0.4 : 1 }}>+ Add</button>
          </div>
          {resp.buttons?.map((btn, bIdx) => (
            <div key={btn.id} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input value={btn.title} onChange={e => onUpdateButton(bIdx, 'title', e.target.value)} placeholder="Button label" style={{ ...S.input, flex: 1 }} />
              <input value={btn.payload} onChange={e => onUpdateButton(bIdx, 'payload', e.target.value)} placeholder="Payload (trigger word)" style={{ ...S.input, flex: 1 }} />
              <button onClick={() => onDeleteButton(bIdx)} style={S.btnDanger}>✕</button>
            </div>
          ))}
          <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>
            When a visitor clicks a button, the payload is sent as their message — make sure it matches a trigger word.
          </div>
        </div>
      )}

      {resp.type === 'image' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <MediaPicker value={resp.content} onChange={url => onUpdate('content', url)} kind="image" placeholder="Image URL (https://...)" />
          <input value={resp.caption || ''} onChange={e => onUpdate('caption', e.target.value)} placeholder="Caption (optional)" style={S.input} />
        </div>
      )}

      {resp.type === 'video' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <MediaPicker value={resp.content} onChange={url => onUpdate('content', url)} kind="video" placeholder="Video URL or YouTube link" />
          <input value={resp.caption || ''} onChange={e => onUpdate('caption', e.target.value)} placeholder="Caption (optional)" style={S.input} />
        </div>
      )}

      {resp.type === 'link' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <textarea value={resp.content} onChange={e => onUpdate('content', e.target.value)} rows={2}
            placeholder="Message shown above the link button" style={S.textarea} />
          <input value={resp.url || ''} onChange={e => onUpdate('url', e.target.value)} placeholder="URL (https://...)" style={S.input} />
          <input value={resp.linkLabel || ''} onChange={e => onUpdate('linkLabel', e.target.value)} placeholder="Button label (e.g. Book now, View menu, Learn more)" style={S.input} />
        </div>
      )}

      {resp.type === 'ask' && (
        <div>
          <input value={resp.content} onChange={e => onUpdate('content', e.target.value)}
            placeholder="Question to ask the visitor (e.g. What is your name?)"
            style={{ ...S.input, marginBottom: '8px' }} />
          <input value={resp.askVariable || ''} onChange={e => onUpdate('askVariable', e.target.value)}
            placeholder="Save answer as (e.g. name, email, phone)"
            style={{ ...S.input, marginBottom: '8px' }} />
          {resp.askVariable && (
            <div style={{ fontSize: '11px', color: '#f59e0b', padding: '8px 12px', background: '#f59e0b10', borderRadius: '8px', marginBottom: '12px' }}>
              Answer saved as <strong>{`{{${resp.askVariable}}}`}</strong> — use this in any Text response
            </div>
          )}
          <div style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: '600', marginBottom: '8px' }}>AFTER VISITOR ANSWERS, SEND:</div>
          <button onClick={onAddAskResponse} style={{ ...S.btnGhost, marginBottom: '10px' }}>+ Add follow-up response</button>
          {resp.askResponses?.map((follow, fIdx) => (
            <div key={follow.id} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <select value={follow.type} onChange={e => onUpdateAskResponse(fIdx, 'type', e.target.value)}
                style={{ ...S.input, flex: 1, width: 'auto', appearance: 'none' as const }}>
                <option value="text">Text</option>
                <option value="buttons">Buttons</option>
                <option value="link">Link</option>
              </select>
              <input value={follow.content} onChange={e => onUpdateAskResponse(fIdx, 'content', e.target.value)}
                placeholder="Response text" style={{ ...S.input, flex: 2 }} />
              <button onClick={() => onDeleteAskResponse(fIdx)} style={S.btnDanger}>✕</button>
            </div>
          ))}
        </div>
      )}

      {resp.type === 'handoff' && (
        <div style={{ background: '#ef444410', border: '1px solid #ef444430', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ color: '#ef4444', fontSize: '12px', fontWeight: '600' }}>Ends the bot conversation and notifies a human agent</div>
          <textarea value={resp.handoffMessage || ''} onChange={e => onUpdate('handoffMessage', e.target.value)} rows={2}
            placeholder="Message shown to the visitor (e.g. Connecting you to our team now...)" style={S.textarea} />
          <input value={resp.handoffEmail || ''} onChange={e => onUpdate('handoffEmail', e.target.value)}
            placeholder="Email to notify when handoff happens (e.g. support@yourbusiness.com)" style={S.input} />
        </div>
      )}

      {resp.type === 'payment' && (
        <div style={{ background: '#a855f710', border: '1px solid #a855f730', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ color: '#a855f7', fontSize: '12px', fontWeight: '600' }}>Sends a payment request to the visitor</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Payment Method</label>
              <select value={resp.paymentMethod || 'paynow'} onChange={e => onUpdate('paymentMethod', e.target.value)}
                style={{ ...S.input, appearance: 'none' as const }}>
                <option value="paynow">Paynow (EcoCash)</option>
                <option value="cash">Cash</option>
                <option value="bank">Bank Transfer</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Currency</label>
              <input value={resp.currency || 'USD'} onChange={e => onUpdate('currency', e.target.value)}
                placeholder="USD" style={S.input} />
            </div>
          </div>
          <div>
            <label style={S.label}>Amount — use {'{{amount}}'} for dynamic</label>
            <input value={resp.amount || ''} onChange={e => onUpdate('amount', e.target.value)}
              placeholder="e.g. 25 or {{amount}}" style={S.input} />
          </div>
          <div>
            <label style={S.label}>Item Name</label>
            <input value={resp.itemName || ''} onChange={e => onUpdate('itemName', e.target.value)}
              placeholder="e.g. Consultation fee" style={S.input} />
          </div>
          <div>
            <label style={S.label}>Description (optional)</label>
            <input value={resp.description || ''} onChange={e => onUpdate('description', e.target.value)}
              placeholder="e.g. Payment for booking #{{bookingId}}" style={S.input} />
          </div>
          <div style={{ fontSize: '11px', color: '#a855f7', padding: '8px 10px', background: '#a855f710', borderRadius: '6px' }}>
            Configure payment credentials (API keys) in Bot Settings → Payment Methods
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function WebsiteBuilder() {
  const { botId } = useParams();
  const navigate = useNavigate();
  const isTemplate = window.location.pathname.includes('/template-builder/');
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [botName, setBotName] = useState('My Website Bot');
  const [greeting, setGreeting] = useState('Hi! How can I help you today?');
  const [offlineMessage, setOfflineMessage] = useState("We're offline right now. Leave a message and we'll get back to you!");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTriggerWord, setNewTriggerWord] = useState('');
  const [newTriggerResponse, setNewTriggerResponse] = useState('');
  const [expandedTriggers, setExpandedTriggers] = useState<Set<number>>(new Set([0]));
  const [activeTab, setActiveTab] = useState<'flow' | 'settings' | 'embed'>('flow');
  const [isAIBot, setIsAIBot] = useState(false);

  const allVariables = triggers.flatMap(t =>
    t.responses.filter(r => r.askVariable).map(r => r.askVariable as string)
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadFlow(); }, [botId]);

  const loadFlow = async () => {
    if (!botId) { setLoading(false); return; }
    try {
      const [botSnap, flowSnap] = await Promise.all([
        get(ref(db, `bots/${botId}`)),
        get(ref(db, isTemplate ? `templates/${botId}` : `flows/${botId}`)),
      ]);
      if (botSnap.exists()) {
        const botData = botSnap.val();
        setBotName(botData.businessName || 'My Website Bot');
        if (botData.botType === 'ai') { setIsAIBot(true); setLoading(false); return; }
      }
      if (flowSnap.exists()) {
        const d = flowSnap.val();
        setTriggers(d.triggers || []);
        if (d.greeting) setGreeting(d.greeting);
        if (d.offlineMessage) setOfflineMessage(d.offlineMessage);
      } else {
        setTriggers([
          {
            trigger: 'hi',
            keywords: ['hello', 'hey', 'start'],
            responses: [{
              id: '1', type: 'buttons',
              content: 'Hi there! How can I help you today?',
              buttons: [
                { id: 'b1', title: 'Our services', payload: 'services' },
                { id: 'b2', title: 'Contact us', payload: 'contact' },
                { id: 'b3', title: 'Pricing', payload: 'pricing' },
              ],
            }],
          },
          {
            trigger: 'contact',
            keywords: ['email', 'phone', 'reach'],
            responses: [{ id: '2', type: 'text', content: "You can reach us at hello@yourbusiness.com\n\nWe're open Mon–Fri, 8am–5pm." }],
          },
        ]);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const saveFlow = async () => {
    if (!auth.currentUser) { alert('Login required'); return; }
    setSaving(true);
    try {
      await set(ref(db, isTemplate ? `templates/${botId}` : `flows/${botId}`), {
        triggers, greeting, offlineMessage,
        botId, userId: auth.currentUser.uid, updatedAt: Date.now(),
      });
      alert('Saved!');
    } catch (e) { alert('Error: ' + e); }
    finally { setSaving(false); }
  };

  const toggleExpand = (idx: number) => {
    setExpandedTriggers(prev => {
      const next = new Set(Array.from(prev));
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const addTrigger = () => {
    if (!newTriggerWord.trim()) { alert('Enter a trigger word'); return; }
    const idx = triggers.length;
    setTriggers([...triggers, {
      trigger: newTriggerWord.toLowerCase().trim(),
      keywords: [],
      responses: [{ id: Date.now().toString(), type: 'text', content: newTriggerResponse || '' }],
    }]);
    setNewTriggerWord('');
    setNewTriggerResponse('');
    setExpandedTriggers(prev => new Set([...Array.from(prev), idx]));
  };

  const updateResponse = (tIdx: number, rIdx: number, field: string, value: any) => {
    const updated = [...triggers];
    updated[tIdx].responses[rIdx] = { ...updated[tIdx].responses[rIdx], [field]: value };
    setTriggers(updated);
  };

  const moveResponseUp = (tIdx: number, rIdx: number) => {
    if (rIdx === 0) return;
    const updated = [...triggers];
    [updated[tIdx].responses[rIdx - 1], updated[tIdx].responses[rIdx]] =
      [updated[tIdx].responses[rIdx], updated[tIdx].responses[rIdx - 1]];
    setTriggers(updated);
  };

  const addButton = (tIdx: number, rIdx: number) => {
    const updated = [...triggers];
    if (!updated[tIdx].responses[rIdx].buttons) updated[tIdx].responses[rIdx].buttons = [];
    if ((updated[tIdx].responses[rIdx].buttons!.length) >= 3) { alert('Max 3 buttons'); return; }
    updated[tIdx].responses[rIdx].buttons!.push({ id: Date.now().toString(), title: 'Option', payload: 'option' });
    setTriggers(updated);
  };

  const updateButton = (tIdx: number, rIdx: number, bIdx: number, field: string, value: string) => {
    const updated = [...triggers];
    updated[tIdx].responses[rIdx].buttons![bIdx] = { ...updated[tIdx].responses[rIdx].buttons![bIdx], [field]: value };
    setTriggers(updated);
  };

  const deleteButton = (tIdx: number, rIdx: number, bIdx: number) => {
    const updated = [...triggers];
    updated[tIdx].responses[rIdx].buttons = updated[tIdx].responses[rIdx].buttons!.filter((_, i) => i !== bIdx);
    setTriggers(updated);
  };

  const addAskResponse = (tIdx: number, rIdx: number) => {
    const updated = [...triggers];
    if (!updated[tIdx].responses[rIdx].askResponses) updated[tIdx].responses[rIdx].askResponses = [];
    updated[tIdx].responses[rIdx].askResponses!.push({ id: Date.now().toString(), type: 'text', content: 'Thank you!' });
    setTriggers(updated);
  };

  const updateAskResponse = (tIdx: number, rIdx: number, fIdx: number, field: string, value: any) => {
    const updated = [...triggers];
    (updated[tIdx].responses[rIdx].askResponses![fIdx] as any)[field] = value;
    setTriggers(updated);
  };

  const deleteAskResponse = (tIdx: number, rIdx: number, fIdx: number) => {
    const updated = [...triggers];
    updated[tIdx].responses[rIdx].askResponses = updated[tIdx].responses[rIdx].askResponses!.filter((_, i) => i !== fIdx);
    setTriggers(updated);
  };

  const applyTemplate = (tmpl: any) => {
    setTriggers(tmpl.triggers || []);
    if (tmpl.greeting) setGreeting(tmpl.greeting);
    if (tmpl.offlineMessage) setOfflineMessage(tmpl.offlineMessage);
  };

  const API_BASE  = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : (process.env.REACT_APP_API_URL || '');
  const widgetURL = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_WIDGET_URL || '');

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg-page)' }}>
      <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Loading flow...</div>
    </div>
  );

  if (isAIBot) return (
    <div style={S.page}>
      <div style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--bg-element)', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: '14px' }}>
        <button onClick={() => navigate('/dashboard')} style={S.btnGhost}>← Dashboard</button>
        <span style={{ fontSize: '18px' }}>🤖</span>
        <span style={{ color: 'var(--text-primary)', fontSize: '16px', fontWeight: '700' }}>{botName}</span>
      </div>
      <div style={{ maxWidth: '520px', margin: '80px auto', textAlign: 'center', padding: '0 24px' }}>
        <div style={{ fontSize: '56px', marginBottom: '20px' }}>🤖</div>
        <h2 style={{ color: 'var(--text-primary)', fontWeight: '700', fontSize: '22px', margin: '0 0 12px' }}>This is an AI bot</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6', margin: '0 0 32px' }}>
          AI bots don't use a trigger-based flow — they respond to every message automatically using AI.
          Configure your knowledge base, system instructions, and API key in Bot Settings.
        </p>
        <button onClick={() => navigate('/dashboard')} style={{ ...S.btnPrimary, fontSize: '14px', padding: '12px 28px' }}>
          Open Bot Settings
        </button>
      </div>
    </div>
  );

  return (
    <>
      <SEO title={`Edit Website Bot | ${botName}`} description="" keywords="" type="website" noindex />
      <div style={S.page}>

        {/* Top bar */}
        <div style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--bg-element)', padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <button onClick={() => navigate(isTemplate ? '/templates' : '/dashboard')} style={S.btnGhost}>← {isTemplate ? 'Templates' : 'Dashboard'}</button>
            <span style={{ fontSize: '18px' }}>🌐</span>
            <span style={{ color: 'var(--text-primary)', fontSize: '16px', fontWeight: '700' }}>{botName}</span>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-faint)' }}>{triggers.length} triggers</div>
            <TemplateSelector botType="website" onApply={applyTemplate} />
            {!isTemplate && <SaveAsTemplate botType="website" triggers={triggers} greeting={greeting} offlineMessage={offlineMessage} />}
            <button onClick={saveFlow} disabled={saving} style={{ ...S.btnPrimary, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : '💾 Save flow'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ borderBottom: '1px solid var(--bg-element)', background: 'var(--bg-card)', padding: '0 24px', display: 'flex', gap: '4px' }}>
          {([['flow', '🔀 Flow Builder'], ['settings', '⚙️ Widget Settings'], ['embed', '🔌 Embed Code']] as const).map(([tab, label]) => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: '12px 18px', background: 'none', border: 'none', cursor: 'pointer',
              color: activeTab === tab ? '#5B4FE8' : 'var(--text-muted)', fontSize: '13px',
              fontWeight: activeTab === tab ? '700' : '400',
              borderBottom: activeTab === tab ? '2px solid #5B4FE8' : '2px solid transparent',
            }}>{label}</button>
          ))}
        </div>

        <div style={{ maxWidth: '860px', margin: '0 auto', padding: '28px 20px' }}>

          {/* ── FLOW BUILDER ── */}
          {activeTab === 'flow' && (
            <>
              {/* Add trigger */}
              <div style={{ ...S.card, borderStyle: 'dashed', borderColor: '#5B4FE840' }}>
                <div style={{ color: 'var(--text-secondary)', fontWeight: '600', fontSize: '13px', marginBottom: '12px' }}>Add a new trigger</div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={S.label}>When visitor types</label>
                    <input value={newTriggerWord} onChange={e => setNewTriggerWord(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addTrigger()}
                      placeholder="e.g. pricing, contact, help" style={S.input} />
                  </div>
                  <div style={{ flex: 2 }}>
                    <label style={S.label}>First response</label>
                    <input value={newTriggerResponse} onChange={e => setNewTriggerResponse(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addTrigger()}
                      placeholder="e.g. Our pricing starts at $15/month..." style={S.input} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button onClick={addTrigger} style={S.btnPrimary}>+ Add</button>
                  </div>
                </div>
              </div>

              {triggers.length === 0 && (
                <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-faint)', fontSize: '14px' }}>
                  No triggers yet. Add one above to get started.
                </div>
              )}

              {triggers.map((trigger, tIdx) => {
                const isExpanded = expandedTriggers.has(tIdx);
                return (
                  <div key={tIdx} style={S.card}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <button onClick={() => toggleExpand(tIdx)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '14px', padding: '4px 0', flexShrink: 0, marginTop: '16px' }}>
                        {isExpanded ? '▾' : '▸'}
                      </button>

                      <div style={{ flex: 1 }}>
                        <label style={S.label}>Trigger word</label>
                        <input value={trigger.trigger}
                          onChange={e => { const u = [...triggers]; u[tIdx].trigger = e.target.value.toLowerCase(); setTriggers(u); }}
                          style={{ ...S.input, fontWeight: '600', color: '#818cf8' }} />
                      </div>

                      <div style={{ flex: 2 }}>
                        <label style={S.label}>Keyword aliases (comma-separated)</label>
                        <input
                          value={trigger.keywords?.join(', ') || ''}
                          onChange={e => { const u = [...triggers]; u[tIdx].keywords = e.target.value.split(',').map(k => k.trim().toLowerCase()).filter(Boolean); setTriggers(u); }}
                          placeholder="e.g. price, cost, how much"
                          style={S.input}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginTop: '18px' }}>
                        <span style={{ fontSize: '11px', color: 'var(--text-faint)', padding: '4px 10px', background: 'var(--bg-element)', borderRadius: '10px' }}>
                          {trigger.responses.length} response{trigger.responses.length !== 1 ? 's' : ''}
                        </span>
                        <button onClick={() => {
                          const src = triggers[tIdx];
                          setTriggers([...triggers, { ...src, trigger: src.trigger + '_copy', responses: src.responses.map(r => ({ ...r, id: Date.now().toString() + Math.random() })) }]);
                        }} style={S.btnGhost}>📋</button>
                        <button onClick={() => {
                          if (!window.confirm(`Delete "${trigger.trigger}"?`)) return;
                          setTriggers(triggers.filter((_, i) => i !== tIdx));
                        }} style={S.btnDanger}>Delete</button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div style={{ marginTop: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <div style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: '600' }}>RESPONSES (sent in order)</div>
                          <button onClick={() => {
                            const u = [...triggers];
                            u[tIdx].responses.push({ id: Date.now().toString(), type: 'text', content: '' });
                            setTriggers(u);
                          }} style={S.btnGreen}>+ Add response</button>
                        </div>

                        {trigger.responses.map((resp, rIdx) => (
                          <ResponseEditor
                            key={resp.id}
                            resp={resp} rIdx={rIdx} allVariables={allVariables}
                            onUpdate={(f, v) => updateResponse(tIdx, rIdx, f, v)}
                            onDelete={() => { const u = [...triggers]; u[tIdx].responses = u[tIdx].responses.filter((_, i) => i !== rIdx); setTriggers(u); }}
                            onMoveUp={() => moveResponseUp(tIdx, rIdx)}
                            onAddButton={() => addButton(tIdx, rIdx)}
                            onUpdateButton={(bIdx, f, v) => updateButton(tIdx, rIdx, bIdx, f, v)}
                            onDeleteButton={bIdx => deleteButton(tIdx, rIdx, bIdx)}
                            onAddAskResponse={() => addAskResponse(tIdx, rIdx)}
                            onUpdateAskResponse={(fIdx, f, v) => updateAskResponse(tIdx, rIdx, fIdx, f, v)}
                            onDeleteAskResponse={fIdx => deleteAskResponse(tIdx, rIdx, fIdx)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* ── SETTINGS ── */}
          {activeTab === 'settings' && (
            <>
              <div style={{ color: 'var(--text-primary)', fontSize: '17px', fontWeight: '700', marginBottom: '20px' }}>Widget settings</div>
              <div style={S.card}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={S.label}>Greeting message</label>
                  <textarea value={greeting} onChange={e => setGreeting(e.target.value)} rows={2}
                    placeholder="Hi! How can I help you today?" style={{ ...S.textarea, marginBottom: '5px' }} />
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)' }}>First message visitors see when they open the chat</div>
                </div>
                <div>
                  <label style={S.label}>No-match / offline message</label>
                  <textarea value={offlineMessage} onChange={e => setOfflineMessage(e.target.value)} rows={2}
                    placeholder="We're offline right now. Leave a message!" style={{ ...S.textarea, marginBottom: '5px' }} />
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)' }}>Shown when the visitor types something that doesn't match any trigger</div>
                </div>
              </div>
              <button onClick={saveFlow} disabled={saving} style={{ ...S.btnPrimary, opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving...' : '💾 Save settings'}
              </button>
            </>
          )}

          {/* ── EMBED ── */}
          {activeTab === 'embed' && (
            <>
              <div style={{ color: 'var(--text-primary)', fontSize: '17px', fontWeight: '700', marginBottom: '20px' }}>Embed your chat widget</div>
              <div style={S.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' }}>Paste this before {'</body>'} on your site</div>
                  <button onClick={() => { navigator.clipboard.writeText(`<script src="${widgetURL}/widget.js" data-bot-id="${botId}" data-api-url="${API_BASE}"></script>`); alert('Copied!'); }} style={S.btnGhost}>Copy</button>
                </div>
                <div style={{ background: 'var(--bg-page)', borderRadius: '8px', padding: '16px', border: '1px solid var(--bg-element)', marginBottom: '16px' }}>
                  <code style={{ color: '#818cf8', fontSize: '13px', wordBreak: 'break-all', display: 'block', lineHeight: '1.6' }}>
                    {`<script src="${widgetURL}/widget.js" data-bot-id="${botId}" data-api-url="${API_BASE}"></script>`}
                  </code>
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600', marginBottom: '10px' }}>Optional attributes</div>
                <div style={{ background: 'var(--bg-page)', borderRadius: '8px', padding: '16px', border: '1px solid var(--bg-element)' }}>
                  <code style={{ color: '#818cf8', fontSize: '12px', display: 'block', lineHeight: '2.2', whiteSpace: 'pre-wrap' }}>
                    {`<script\n  src="${widgetURL}/widget.js"\n  data-bot-id="${botId}"\n  data-api-url="${API_BASE}"\n  data-primary-color="#5B4FE8"\n  data-position="bottom-right"\n  data-business-name="${botName}"\n></script>`}
                  </code>
                </div>
              </div>
              <div style={S.card}>
                <div style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600', marginBottom: '16px' }}>How to install</div>
                {[
                  { n: '1', t: 'Copy the code', d: 'Click Copy above to copy your unique embed snippet.' },
                  { n: '2', t: 'Open your website editor', d: 'Works on any site — WordPress, Wix, Squarespace, or custom HTML.' },
                  { n: '3', t: 'Paste before </body>', d: 'Find the closing </body> tag in your theme footer and paste the code just before it.' },
                  { n: '4', t: 'Publish and test', d: 'Save and publish. The chat bubble appears in the bottom-right corner.' },
                ].map(s => (
                  <div key={s.n} style={{ display: 'flex', gap: '14px', marginBottom: '14px', alignItems: 'flex-start' }}>
                    <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: '#5B4FE8', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0 }}>{s.n}</div>
                    <div>
                      <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: '600', marginBottom: '2px' }}>{s.t}</div>
                      <div style={{ color: 'var(--text-faint)', fontSize: '12px', lineHeight: '1.5' }}>{s.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
